import type { Results, ResultsRecord } from '@shared/models/unit.model';
import type {
  Action,
  ProjectChange,
} from '@shared/helpers/pipeline/update-engine.models';
import { ProjectChangeType } from '@shared/helpers/pipeline/update-engine.models';
import type { IProposal } from '@shared/models/proposals.model';
import type { Baseline } from '@shared/models/project.model';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import {
  isDeleted,
  isDeactivatedInElementTree,
} from '@shared/helpers/element.helpers';
import { getElementById } from '@shared/helpers/recursive-element.helpers';
import {
  getAffectedElementIds,
  getChangeAffectedElementId,
  getChangeAttributionHostElementId,
} from './action.helpers';

const LIFECYCLE_PROJECT_CHANGE_TYPES = new Set<ProjectChangeType>([
  ProjectChangeType.ElementActivate,
  ProjectChangeType.ElementDeactivate,
  ProjectChangeType.ElementSoftDelete,
  ProjectChangeType.ElementRemove,
]);

/**
 * Baseline vs proposal-resolved trees so {@link computeElementDiffs} can infer
 * lifecycle deltas when per-element result rows stay equal but rollups change.
 */
export type ProposalElementRollupContext = {
  baselineVersion: Baseline;
  proposalResolvedVersion: Baseline;
};

/**
 * True when {@link elementId} exists on {@link tree} and is included in parent
 * rollups (not deleted, not deactivated on self or any ancestor).
 */
const elementContributesToVersionRollup = (
  tree: Baseline,
  elementId: string,
): boolean => {
  const el = getElementById(tree, elementId, false);
  if (!el || isDeleted(el)) return false;
  if (isDeactivatedInElementTree(tree, elementId)) return false;
  return true;
};

/**
 * Collects proposal changes that target {@link elementId} via
 * {@link getChangeAffectedElementId}.
 */
const getChangesTargetingElement = (
  relevantActions: Action[],
  elementId: string,
): ProjectChange[] => {
  const out: ProjectChange[] = [];
  for (const a of relevantActions) {
    for (const ch of a.changes) {
      if (getChangeAffectedElementId(ch) === elementId) out.push(ch);
    }
  }
  return out;
};

/**
 * When every change on {@link elementId} is activate/deactivate/delete-only,
 * returns signed CO2e/cost deltas from inclusion flip × element row magnitude;
 * otherwise `undefined` so callers keep naive record diffs.
 */
const tryLifecycleStructuralDeltas = (
  elementId: string,
  relevantActions: Action[],
  rollup: ProposalElementRollupContext,
  baselineRecord: ResultsRecord,
  proposalRecord: ResultsRecord,
): { deltaCo2e: number; deltaCost: number } | undefined => {
  const targeted = getChangesTargetingElement(relevantActions, elementId);
  if (targeted.length === 0) return undefined;
  if (!targeted.every((ch) => LIFECYCLE_PROJECT_CHANGE_TYPES.has(ch.type)))
    return undefined;

  const cBase = elementContributesToVersionRollup(
    rollup.baselineVersion,
    elementId,
  );
  const cProp = elementContributesToVersionRollup(
    rollup.proposalResolvedVersion,
    elementId,
  );
  if (cBase === cProp) return undefined;

  const bCo2 =
    getConversionFactorValue(baselineRecord[elementId] ?? {}, 'co2e') ?? 0;
  const pCo2 =
    getConversionFactorValue(proposalRecord[elementId] ?? {}, 'co2e') ?? 0;
  const magCo2 = Math.max(bCo2, pCo2);

  const bSek =
    getConversionFactorValue(baselineRecord[elementId] ?? {}, 'sek') ?? 0;
  const pSek =
    getConversionFactorValue(proposalRecord[elementId] ?? {}, 'sek') ?? 0;
  const magSek = Math.max(bSek, pSek);

  // Lost from project total vs gained back (activate)
  let sign: number;
  if (!cBase && cProp) sign = 1;
  else if (cBase && !cProp) sign = -1;
  else return undefined;

  return { deltaCo2e: sign * magCo2, deltaCost: sign * magSek };
};

/**
 * Per-element diff between proposal and baseline results.
 */
export type ElementDiff = {
  elementId: string;
  baseline: Results;
  proposal: Results;
  deltaCo2e: number;
  deltaCost: number;
  /**
   * When false a numeric CO₂/cost delta is not meaningful (e.g. element added by
   * recipe or removed from the proposal tree); UI should show "-".
   */
  hasComparableResults: boolean;
};

/**
 * True when {@link elementId} exists in the baseline results record and, when
 * {@link rollupContext} is set, on the baseline version tree.
 */
export const hasComparableElementBaseline = (
  elementId: string,
  baselineRecord: ResultsRecord,
  rollupContext?: ProposalElementRollupContext,
): boolean => {
  if (!Object.prototype.hasOwnProperty.call(baselineRecord, elementId)) {
    return false;
  }
  if (!rollupContext) return true;
  return (
    getElementById(rollupContext.baselineVersion, elementId, false) !==
    undefined
  );
};

/**
 * True when {@link elementId} exists in the proposal results record and, when
 * {@link rollupContext} is set, on the proposal-resolved version tree.
 */
export const hasComparableElementProposal = (
  elementId: string,
  proposalRecord: ResultsRecord,
  rollupContext?: ProposalElementRollupContext,
): boolean => {
  if (!Object.prototype.hasOwnProperty.call(proposalRecord, elementId)) {
    return false;
  }
  if (!rollupContext) return true;
  return (
    getElementById(rollupContext.proposalResolvedVersion, elementId, false) !==
    undefined
  );
};

/**
 * Both sides have a results row (and tree slot when {@link rollupContext} is set).
 */
export const hasComparableElementResults = (
  elementId: string,
  baselineRecord: ResultsRecord,
  proposalRecord: ResultsRecord,
  rollupContext?: ProposalElementRollupContext,
): boolean =>
  hasComparableElementBaseline(elementId, baselineRecord, rollupContext) &&
  hasComparableElementProposal(elementId, proposalRecord, rollupContext);

/**
 * Compute per-element diffs for elements affected by a proposal's actions.
 *
 * Only elements targeted by at least one {@link Action} change are included.
 * Each diff contains the baseline result, proposal result, and numeric deltas
 * for CO2e and cost (SEK).
 *
 * @param proposal - The selected proposal
 * @param actions - All actions on the version (superset)
 * @param baselineRecord - ResultsRecord from the baseline version
 * @param proposalRecord - ResultsRecord from the proposal (populated by pipeline)
 * @param rollupContext - When set, lifecycle-only rows use tree inclusion vs
 *                        identical per-id result slots (fixes DEV-3251 KPI split).
 */
export const computeElementDiffs = (
  proposal: IProposal,
  actions: Action[],
  baselineRecord: ResultsRecord,
  proposalRecord: ResultsRecord,
  rollupContext?: ProposalElementRollupContext,
): ElementDiff[] => {
  const actionIdSet = new Set(proposal.actionIds);
  const relevantActions = actions.filter((a) => actionIdSet.has(a.id));
  const allChanges = relevantActions.flatMap((a) => a.changes);
  const affectedIds = getAffectedElementIds(allChanges);

  return affectedIds.map((elementId) => {
    const baseline = baselineRecord[elementId] ?? {};
    const proposal = proposalRecord[elementId] ?? {};

    if (rollupContext) {
      const structural = tryLifecycleStructuralDeltas(
        elementId,
        relevantActions,
        rollupContext,
        baselineRecord,
        proposalRecord,
      );
      if (structural) {
        return {
          elementId,
          baseline,
          proposal,
          deltaCo2e: structural.deltaCo2e,
          deltaCost: structural.deltaCost,
          hasComparableResults: true,
        };
      }
    }

    if (
      !hasComparableElementResults(
        elementId,
        baselineRecord,
        proposalRecord,
        rollupContext,
      )
    ) {
      return {
        elementId,
        baseline,
        proposal,
        deltaCo2e: 0,
        deltaCost: 0,
        hasComparableResults: false,
      };
    }

    return {
      elementId,
      baseline,
      proposal,
      deltaCo2e:
        getConversionFactorValue(proposal, 'co2e') -
        getConversionFactorValue(baseline, 'co2e'),
      deltaCost:
        getConversionFactorValue(proposal, 'sek') -
        getConversionFactorValue(baseline, 'sek'),
      hasComparableResults: true,
    };
  });
};

const EPS = 1e-9;

export type PerActionKpiDiffKey = keyof Pick<
  ElementDiff,
  'deltaCo2e' | 'deltaCost'
>;

/** How many {@link ProjectChange}s in {@link changes} target {@link hostId}. */
const countChangesTargetingAttributionHost = (
  changes: ProjectChange[],
  hostId: string,
): number => {
  let n = 0;
  for (const ch of changes) {
    if (getChangeAttributionHostElementId(ch) === hostId) n++;
  }
  return n;
};

/** Per host: total changes across all actions that target it (recipe children roll up). */
const buildTotalAttributionHostTouchCount = (
  relevantActions: Action[],
): Map<string, number> => {
  const out = new Map<string, number>();
  for (const a of relevantActions) {
    for (const ch of a.changes) {
      const host = getChangeAttributionHostElementId(ch);
      if (!host) continue;
      out.set(host, (out.get(host) ?? 0) + 1);
    }
  }
  return out;
};

/** Unique attribution hosts touched by {@link changes}. */
const getAttributionHostIds = (changes: ProjectChange[]): string[] => {
  const ids = new Set<string>();
  for (const ch of changes) {
    const host = getChangeAttributionHostElementId(ch);
    if (host) ids.add(host);
  }
  return [...ids];
};

const computePerActionShareFromElementDiffs = (
  elementDiffs: ElementDiff[],
  relevantActions: Action[],
  kpiDelta: number,
  diffKey: PerActionKpiDiffKey,
): Record<string, number> => {
  if (relevantActions.length === 0) return {};

  const out: Record<string, number> = Object.fromEntries(
    relevantActions.map((a) => [a.id, 0]),
  );
  const raw: Record<string, number> = Object.fromEntries(
    relevantActions.map((a) => [a.id, 0]),
  );

  const diffByElement = new Map(
    elementDiffs
      .filter((d) => d.hasComparableResults)
      .map((d) => [d.elementId, d[diffKey]]),
  );
  const totalHostTouches = buildTotalAttributionHostTouchCount(relevantActions);

  for (const a of relevantActions) {
    let s = 0;
    for (const host of getAttributionHostIds(a.changes)) {
      const d = diffByElement.get(host);
      const total = totalHostTouches.get(host) ?? 0;
      const actionTouches = countChangesTargetingAttributionHost(
        a.changes,
        host,
      );
      if (d !== undefined && total > 0 && actionTouches > 0) {
        s += d * (actionTouches / total);
      }
    }
    raw[a.id] = s;
  }

  const sumRaw = relevantActions.reduce((acc, a) => acc + (raw[a.id] ?? 0), 0);

  if (Math.abs(sumRaw) < EPS) {
    if (Math.abs(kpiDelta) < EPS) return out;
    const each = kpiDelta / relevantActions.length;
    for (const a of relevantActions) {
      out[a.id] = each;
    }
    return out;
  }

  const factor = kpiDelta / sumRaw;
  for (const a of relevantActions) {
    out[a.id] = (raw[a.id] ?? 0) * factor;
  }
  return out;
};

/**
 * Splits a project-level KPI delta across the proposal's actions: each
 * element's delta for the chosen dimension is shared among changes that target
 * that element (multiple changes in one action each get a share), then values
 * are scaled so their sum matches `kpiDelta`.
 */
export const computePerActionKpiDeltaShare = (
  proposal: IProposal,
  actions: Action[],
  baselineRecord: ResultsRecord,
  proposalRecord: ResultsRecord,
  kpiDelta: number,
  diffKey: PerActionKpiDiffKey,
  rollupContext?: ProposalElementRollupContext,
): Record<string, number> => {
  const actionIdSet = new Set(proposal.actionIds);
  const relevantActions = actions.filter((a) => actionIdSet.has(a.id));
  const elementDiffs = computeElementDiffs(
    proposal,
    actions,
    baselineRecord,
    proposalRecord,
    rollupContext,
  );
  return computePerActionShareFromElementDiffs(
    elementDiffs,
    relevantActions,
    kpiDelta,
    diffKey,
  );
};

/** Same as {@link computePerActionKpiDeltaShare} with `'deltaCost'`. */
export const computePerActionCostDeltaSek = (
  proposal: IProposal,
  actions: Action[],
  baselineRecord: ResultsRecord,
  proposalRecord: ResultsRecord,
  kpiCostDelta: number,
  rollupContext?: ProposalElementRollupContext,
): Record<string, number> => {
  return computePerActionKpiDeltaShare(
    proposal,
    actions,
    baselineRecord,
    proposalRecord,
    kpiCostDelta,
    'deltaCost',
    rollupContext,
  );
};
